import { useCallback, useRef, type ComponentProps } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { FlightSortOptions } from 'constants/flight'
import useFlightSortOptions from 'hooks/Flights/useFlightSortOptions'
import { getOtherSortOptions } from 'lib/flights/flightUtils'
import ToggleOptionsGroup from 'LuxKit/ToggleOptionsGroup'
import ToggleOption from 'LuxKit/ToggleOption'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidDirectionIcon from 'LuxKit/Icons/solid/SolidDirectionIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineRowDoubleIcon from 'LuxKit/Icons/line/LineRowDoubleIcon'
import LineRowStackIcon from 'LuxKit/Icons/line/LineRowStackIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import RadioButton from 'LuxKit/Radio/RadioButton'
import useToggle from 'hooks/useToggle'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import BodyText from 'LuxKit/Typography/BodyText'
import noop from 'lib/function/noop'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const FlightSortingToggleOption = styled(ToggleOption)`
  border-bottom: ${rem(3)} solid transparent;
  border-radius: 0;
  &&& {
    box-shadow: none;
  }

  &:not(:first-child) {
    border-inline-start: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  && + && {
    margin-inline-start: 0;
  }

  &:first-child {
    border-end-start-radius: ${props => props.theme.borderRadius.M};
    border-start-start-radius: ${props => props.theme.borderRadius.M};
  }

  &:last-child {
    border-end-end-radius: ${props => props.theme.borderRadius.M};
    border-start-end-radius: ${props => props.theme.borderRadius.M};
  }

  &.is-selected {
    border-bottom-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    &:not(.is-loading) {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

      body.${REBRAND_PALETTE_CLASS} & {
        background-color: var(--palette-neutral-surface-light-background-alt);
      }
    }
  }
`

const ViewToggleOption = styled(FlightSortingToggleOption)`
  min-width: max-content;
`

const ViewDropdownOptions = styled.div`
  width: ${rem(232)};
  margin-block: -${rem(8)};
`

const ViewDropdownOption = styled(ClickableListItem)`
  border-radius: 0;
  gap: ${rem(8)};

  && {
    padding-block: ${rem(10)};
    padding-inline: ${rem(12)};
  }

  &.selected {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

    body.${REBRAND_PALETTE_CLASS} & {
      background-color: var(--palette-neutral-surface-light-background-alt);
    }
  }
`

const FlightSortingBarContainer = styled.div`
  overflow: hidden;
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CheapestPrice = styled.span`
  color: ${props => props.theme.palette.messaging.success.darkForeground};
`

interface SortOptionSubtitleProps {
  price: number
  hours: number
  minutes: number
  isAverageDuration: boolean
  highlightCheapest?: boolean
}

function SortOptionSubtitle(props: SortOptionSubtitleProps) {
  const {
    price,
    hours,
    minutes,
    isAverageDuration,
    highlightCheapest,
  } = props

  const formattedPrice = highlightCheapest ? (
    <CheapestPrice>
      <FormatCurrency value={price} format="roundedDollar" />
    </CheapestPrice>
  ) : (
    <FormatCurrency value={price} format="roundedDollar" />
  )

  if (isAverageDuration) {
    return (
      <FormattedMessage
        id="_nQwOnutXTUHkCop96b1"
        defaultMessage="{price} · {hours}h {minutes}m (Avg)"
        description="Flight sorting option subtitle showing price and average outbound and return duration"
        values={{
          price: formattedPrice,
          hours,
          minutes,
        }}
      />
    )
  }

  return (
    <FormattedMessage
      id="fRphjVfNe-zHRtidBDEQ"
      defaultMessage="{price} · {hours}h {minutes}m"
      description="Flight sorting option subtitle showing the price and total duration for each flight"
      values={{
        price: formattedPrice,
        hours,
        minutes,
      }}
    />
  )
}

interface Props {
  sort?: FlightSortOptions
  destinationAirportCode: string
  originAirportCode: string
  onChange?: (newSort: FlightSortOptions) => void
  optionValues: Record<FlightSortOptions, { time: { hours: number, minutes: number }, price: number }>
  loading?: boolean
  showViewToggle?: boolean
  isCombinedTileView?: boolean
  onViewToggle?: () => void
  dropdownBoundaryRef?: ComponentProps<typeof DropdownList>['boundaryRef']
  dropdownPortaled?: boolean
  dropdownAutoHeight?: boolean
}

function FlightSortingBar(props: Props) {
  const {
    sort = FlightSortOptions.Best,
    onChange = noop,
    originAirportCode,
    destinationAirportCode,
    optionValues,
    loading,
    showViewToggle = false,
    isCombinedTileView = false,
    onViewToggle = noop,
    dropdownBoundaryRef,
    dropdownPortaled,
    dropdownAutoHeight,
  } = props
  const intl = useIntl()
  const sortOptions = useFlightSortOptions()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, toggleDropDown, _, closeDropdown] = useToggle()
  const viewTriggerRef = useRef<HTMLButtonElement>(null)
  const { value: isViewDropdownOpen, toggle: toggleViewDropdown, off: closeViewDropdown } = useToggle()

  const otherSortOptions = getOtherSortOptions(
    originAirportCode,
    destinationAirportCode,
  )

  const combinedResultsLabel = intl.formatMessage({
    id: 'ODlqkdGnui_2umU-qbkj',
    defaultMessage: 'Combined results',
    description: 'Flight results view option displaying outbound and return flights together',
  })
  const individualResultsLabel = intl.formatMessage({
    id: 'ydxQ_xAQ2RyfXikleIdm',
    defaultMessage: 'Individual results',
    description: 'Flight results view option displaying each flight leg separately',
  })
  const combinedResultsDescription = intl.formatMessage({
    id: 'ytgiBR2hnJEpcbSBoL4M',
    defaultMessage: 'Select flights together',
    description: 'Helper text for the combined results option in the flight view dropdown',
  })
  const individualResultsDescription = intl.formatMessage({
    id: 'G5yFlfE70ixFG5dUpeBB',
    defaultMessage: 'Select flight legs individually',
    description: 'Helper text for the individual results option in the flight view dropdown',
  })

  const handleViewChange = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const isCombinedSelected = event.currentTarget.dataset.view === 'combined'
    if (isCombinedSelected !== isCombinedTileView) {
      onViewToggle()
    }
    closeViewDropdown()
  }, [isCombinedTileView, onViewToggle, closeViewDropdown])

  const handleClick = (
    evt: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const key = evt.currentTarget.dataset.key
    if (key) {
      onChange(key as FlightSortOptions)
      closeDropdown()
    }
  }

  const otherSortingSelected = otherSortOptions.find((s) => s.value === sort)

  const otherSortingTitle = otherSortingSelected ? otherSortingSelected.label : intl.formatMessage({
    id: 'tdHD1_JGpTHz91egUlwa',
    defaultMessage: 'Other sorting',
    description: 'Fallback title for the other sorting options toggle in flight sorting bar',
  })

  const otherSubtitle = otherSortingSelected ? (
    <SortOptionSubtitle
      price={optionValues[sort].price}
      hours={optionValues[sort].time.hours}
      minutes={optionValues[sort].time.minutes}
      isAverageDuration={isCombinedTileView}
    />
  ) : null

  return (
    <>
      <FlightSortingBarContainer>
        <ToggleOptionsGroup horizontalAlign="stretch">
          {sortOptions.map((option) => (
            <FlightSortingToggleOption
              key={option.value}
              className={loading ? 'is-loading' : undefined}
              data-key={option.value}
              title={option.label}
              subtitle={
                <>
                  {loading && (
                    <BodyText variant="small">
                      <TextLoadingBox width="12ch" />
                    </BodyText>
                  )}

                  {!loading && (
                    <SortOptionSubtitle
                      price={optionValues[option.value].price}
                      hours={optionValues[option.value].time.hours}
                      minutes={optionValues[option.value].time.minutes}
                      isAverageDuration={isCombinedTileView}
                      highlightCheapest={option.value === FlightSortOptions.Cheapest}
                    />
                  )}
                </>
              }
              onClick={handleClick}
              isSelected={option.value === sort}
              disabled={loading}
            />
          ))}

          <FlightSortingToggleOption
            ref={triggerRef}
            className={loading ? 'is-loading' : undefined}
            endIcon={<SolidDirectionIcon size="XS" />}
            title={otherSortingTitle}
            titleWrap="word-wrap"
            subtitle={otherSubtitle}
            onClick={toggleDropDown}
            isSelected={Boolean(otherSortingSelected)}
            disabled={loading}
          />

          {showViewToggle && <ViewToggleOption
            ref={viewTriggerRef}
            endIcon={isViewDropdownOpen ? <LineAngleUpIcon size="XS" /> : <LineAngleDownIcon size="XS" />}
            title={isCombinedTileView ? combinedResultsLabel : individualResultsLabel}
            onClick={toggleViewDropdown}
            aria-expanded={isViewDropdownOpen}
            aria-haspopup="true"
          />}
        </ToggleOptionsGroup>
      </FlightSortingBarContainer>

      {showViewToggle && <DropdownList
        size="S"
        anchorRef={viewTriggerRef}
        triggerRef={viewTriggerRef}
        placement="bottom-end"
        open={isViewDropdownOpen}
        onClose={closeViewDropdown}
        boundaryRef={dropdownBoundaryRef}
        portaled={dropdownPortaled}
        autoHeight={dropdownAutoHeight}
        crossAxisShift={dropdownPortaled}
      >
        <ViewDropdownOptions>
          <ViewDropdownOption
            size="medium"
            data-view="combined"
            title={combinedResultsLabel}
            subtitle={combinedResultsDescription}
            endIcon={<LineRowDoubleIcon size="XS" />}
            selected={isCombinedTileView}
            aria-pressed={isCombinedTileView}
            onClick={handleViewChange}
          />
          <ViewDropdownOption
            size="medium"
            data-view="individual"
            title={individualResultsLabel}
            subtitle={individualResultsDescription}
            endIcon={<LineRowStackIcon size="XS" />}
            selected={!isCombinedTileView}
            aria-pressed={!isCombinedTileView}
            onClick={handleViewChange}
          />
        </ViewDropdownOptions>
      </DropdownList>}

      <DropdownList
        size="S"
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        open={isDropdownOpen}
        onClose={closeDropdown}
        boundaryRef={dropdownBoundaryRef}
        portaled={dropdownPortaled}
        autoHeight={dropdownAutoHeight}
        crossAxisShift={dropdownPortaled}
      >
        {otherSortOptions.map((option) => (
          <ClickableListItem
            key={option.value}
            data-key={option.value}
            onClick={handleClick}
            startIcon={<RadioButton checked={option.value === sort} />}
            subtitle={
              <SortOptionSubtitle
                price={optionValues[option.value].price}
                hours={optionValues[option.value].time.hours}
                minutes={optionValues[option.value].time.minutes}
                isAverageDuration={isCombinedTileView}
              />
            }
            selected={option.value === sort}
            title={option.label}
          />
        ))}
      </DropdownList>
    </>
  )
}

export default FlightSortingBar
